import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, ArrowUp, ArrowDown, X, Plus } from "lucide-react";


function GalleryItem({ item, i, total, update, move, remove }) {
  return (
    <div className="flex gap-3 p-2 border border-slate-200 dark:border-slate-800 rounded-sm" data-testid={`gallery-item-${i}`}>
      <img src={item.url} alt="" className="w-20 h-16 object-cover rounded-sm bg-slate-100 shrink-0" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <Input value={item.caption || ""} placeholder="Didascalia (IT)" onChange={(e) => update(i, { caption: e.target.value })} data-testid={`gallery-caption-${i}`} />
        <Input value={item.caption_en || ""} placeholder="Caption (EN)" onChange={(e) => update(i, { caption_en: e.target.value })} data-testid={`gallery-caption-en-${i}`} />
      </div>
      <div className="flex flex-col gap-1">
        <button type="button" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`gallery-up-${i}`} className="p-1 border rounded-sm disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-white/10"><ArrowUp size={14} /></button>
        <button type="button" disabled={i === total - 1} onClick={() => move(i, 1)} data-testid={`gallery-down-${i}`} className="p-1 border rounded-sm disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-white/10"><ArrowDown size={14} /></button>
        <button type="button" onClick={() => remove(i)} data-testid={`gallery-remove-${i}`} className="p-1 border rounded-sm text-legend-red hover:bg-legend-red hover:text-white"><X size={14} /></button>
      </div>
    </div>
  );
}

export function GalleryField({ label, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const update = (i, patch) => onChange(value.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const move = (i, d) => { const n = [...value]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setUploading(true);
    const added = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ url: data.url, caption: "", caption_en: "" });
      } catch { toast.error(`Upload fallito: ${file.name}`); }
    }
    onChange([...value, ...added]);
    if (added.length) toast.success(`${added.length} foto aggiunte!`);
    setUploading(false);
    e.target.value = "";
  };
  const addUrl = () => { if (url.trim()) { onChange([...value, { url: url.trim(), caption: "", caption_en: "" }]); setUrl(""); } };
  return (
    <div data-testid="gallery-field">
      <Label>{label} ({value.length})</Label>
      <div className="space-y-2 mt-1">
        {value.map((g, i) => <GalleryItem key={`${g.url}-${i}`} item={g} i={i} total={value.length} update={update} move={move} remove={remove} />)}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <label className="inline-flex items-center gap-2 cursor-pointer bg-legend-ink text-white text-sm font-bold uppercase px-3 py-2 rounded-sm hover:bg-slate-800">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Carica foto
          <input type="file" accept="image/*" multiple className="hidden" data-testid="gallery-upload" onChange={upload} />
        </label>
        <Input className="flex-1 min-w-[180px]" placeholder="oppure incolla URL foto" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="gallery-url-input" />
        <button type="button" onClick={addUrl} data-testid="gallery-add-url" className="inline-flex items-center gap-1 border px-3 py-2 rounded-sm text-sm font-semibold hover:bg-slate-100 dark:hover:bg-white/10"><Plus size={14} /> Aggiungi</button>
      </div>
    </div>
  );
}

export function RelatedField({ label, options, value, onChange }) {
  const toggle = (slug) => onChange(value.includes(slug) ? value.filter((s) => s !== slug) : value.length < 3 ? [...value, slug] : value);
  return (
    <div data-testid="related-field">
      <Label>{label} ({value.length}/3)</Label>
      <div className="mt-1 max-h-44 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-sm divide-y divide-slate-100 dark:divide-slate-800">
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5">
            <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} disabled={!value.includes(o.value) && value.length >= 3} className="w-4 h-4 accent-legend-red" data-testid={`related-${o.value}`} />
            <span className="truncate">{o.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
